import { isSSR } from 'src/helpers/ssr';
import type { CondPairUnary } from 'lodash';
import { useInitSocialNetworkHandlers } from 'src/components/AuthNew/hooks/useInitSocialNetworkHandlers';
import { useVKAuth } from 'src/components/AuthNew/hooks/useVKAuth';
import { useGithubAuth } from 'src/components/AuthNew/hooks/useGithubAuth';
import { useYandexAuth } from 'src/components/AuthNew/hooks/useYandexAuth';
import { useOKAuth } from 'src/components/AuthNew/hooks/useOKAuth';
import { useTBankAuth } from 'src/components/AuthNew/hooks/useTBankAuth';
import { useAppleAuth } from 'src/components/AuthNew/hooks/useAppleAuth';
import type {
  HandleGetNewCode,
  SetFormType,
  SetSharedState,
} from 'src/components/AuthNew/types';
import {
  AuthFormType,
  AuthProvider,
  RequestStatus,
  AuthMethodsForAnalytics,
} from 'src/components/AuthNew/types';
import type { FullClientData } from '@lt/api/dist/types/client';
import type {
  PostClientAuthResponse,
  VKAuthResponse,
  YandexAuthResponse,
  OKAuthResponse,
  GithubAuthResponse,
  TBankAuthResponse,
  AppleAuthResponse,
  AlfaAuthResponse,
} from '@lt/api/dist/methods/client/types';
import { api } from 'src/api';
import { flowAsync } from 'src/utils/fp';
import { cond } from 'lodash/fp';

import { sendCommonEvents } from 'src/helpers/sendCommonEvents';
import type {
  SocialNetworkNames,
  VKAuthConfig,
  GithubAuthConfig,
  YandexAuthConfig,
  OKAuthConfig,
  TBankAuthConfig,
  AppleAuthConfig,
  AlfaAuthConfig,
} from '../utils/socialNetworkUtils';
import { getSocialAuthConfigByName } from '../utils/socialNetworkUtils';
import {
  isPhoneConfirm,
  isPhoneNotConfirm,
  resetPhoneFormat,
  openConfirmPhoneWithSMSCode,
  mergeFullClientDataWithSharedState,
} from '../utils';
import { pushAuthSuccessful } from '../analytics';
import { useAlfaAuth } from './useAlfaAuth';

type Props = {
  onSuccess: (data: FullClientData) => void;
  setRequestStatus: (type: RequestStatus) => void;
  setFormType: SetFormType;
  setSharedState: SetSharedState;
  handleGetNewCode: HandleGetNewCode;
};

const authWithVKProvider = (code: VKAuthResponse) =>
  api.postClientAuth({
    token: code,
    provider: 'vkontakte',
    use_cookies: true,
  });

const authWithYandexProvider = (code: YandexAuthResponse) =>
  api.postClientAuth({
    token: code,
    provider: 'yandex',
    use_cookies: true,
  });

const authWithOKProvider = (code: OKAuthResponse) =>
  api.postClientAuth({
    token: code,
    provider: 'odnoklassniki',
    use_cookies: true,
  });

const authWithGithubProvider = (code: GithubAuthResponse) =>
  api.postClientAuth({
    token: code,
    provider: 'github',
    use_cookies: true,
  });

const authWithTBankProvider = (token: TBankAuthResponse) =>
  api.postClientAuth({
    token,
    provider: 'tbank',
    use_cookies: true,
  });

const authWithAppleProvider = (code: AppleAuthResponse) =>
  api.postClientAuth({
    token: code,
    provider: 'apple',
    use_cookies: true,
  });

const authWithAlfaProvider = (code: AlfaAuthResponse) =>
  api.postClientAuth({
    token: code,
    provider: 'alfabank',
    use_cookies: true,
  });

const ifAuthNotSuccess = (): CondPairUnary<
  PostClientAuthResponse,
  undefined
> => [
  (response: PostClientAuthResponse) => !response.success,
  () => {
    throw new Error('Social auth not success');
  },
];

const ifHasRegistrationProcessedTrue =
  (handleGetNewCode: HandleGetNewCode) =>
  (setSharedState: SetSharedState) =>
  (setFormType: SetFormType) =>
  (
    onSuccess: Props['onSuccess'],
  ): CondPairUnary<PostClientAuthResponse, Promise<undefined> | undefined> =>
    [
      (response: PostClientAuthResponse) =>
        response.client.has_registration_processed,
      cond<PostClientAuthResponse, Promise<undefined> | undefined>([
        [
          isPhoneNotConfirm,
          async (response: PostClientAuthResponse) => {
            setSharedState(mergeFullClientDataWithSharedState(response.client));
            onSuccess(response.client);
            await handleGetNewCode({
              phone: resetPhoneFormat(response.client.unconfirmed_phone),
              event: 'sign_up',
              client_id: response.client.id,
            });
            openConfirmPhoneWithSMSCode(setFormType)();
            return undefined;
          },
        ],
        [
          isPhoneConfirm,
          (response: PostClientAuthResponse) => {
            onSuccess(response.client);
            return undefined;
          },
        ],
      ]),
    ];

const ifHasRegistrationProcessedFalse =
  (setFormType: Props['setFormType'], authProvider: AuthProvider) =>
  (
    setSharedState: Props['setSharedState'],
  ): CondPairUnary<PostClientAuthResponse, undefined> =>
    [
      (response: PostClientAuthResponse) =>
        !response.client.has_registration_processed,
      (response) => {
        setSharedState({
          password: '',
          email: response.client.email || '',
          phone: response.client.phone || '',
          surname: response.client.surname || '',
          name: response.client.name || '',
          id: response.client.id,
          signUpType: authProvider,
        });

        setFormType(AuthFormType.Registration);
        return undefined;
      },
    ];

export const useSocialAuth = ({
  onSuccess,
  setRequestStatus,
  setSharedState,
  setFormType,
  handleGetNewCode,
}: Props) => {
  useInitSocialNetworkHandlers();

  const { handleVKLogin } = useVKAuth();
  const { handleYandexLogin } = useYandexAuth();
  const { handleOKLogin } = useOKAuth();
  const { handleGithubLogin } = useGithubAuth();
  const { handleTBankLogin } = useTBankAuth();
  const { handleAppleLogin } = useAppleAuth();
  const { handleAlfaLogin } = useAlfaAuth();

  const loginSocialNetwork = (socialNetwork: SocialNetworkNames): void => {
    if (isSSR) {
      return;
    }

    const socialConfig = getSocialAuthConfigByName(socialNetwork);

    try {
      if (!socialConfig) return;

      setRequestStatus(RequestStatus.Submitting);

      if (!socialConfig) return;

      if (socialNetwork === 'vk') {
        sendCommonEvents('oauth_start', 'vk');
        handleVKLogin(
          socialConfig as VKAuthConfig,
          (data) => {
            if (data.success) {
              pushAuthSuccessful(AuthMethodsForAnalytics.VK);
              sendCommonEvents('oauth', 'vk');
              handleOAuthSuccess(socialNetwork, {
                code: data.code,
              });
            }
          },
          () => {
            setRequestStatus(RequestStatus.Failed);
          },
        );
      } else if (socialNetwork === 'yandex') {
        sendCommonEvents('oauth_start', 'yandex');
        handleYandexLogin(
          socialConfig as YandexAuthConfig,
          (data) => {
            if (data.success) {
              pushAuthSuccessful(AuthMethodsForAnalytics.Yandex);
              sendCommonEvents('oauth', 'yandex');
              handleOAuthSuccess(socialNetwork, {
                access_token: data.access_token,
                expires_in: data.expires_in,
              });
            }
          },
          () => {
            setRequestStatus(RequestStatus.Failed);
          },
        );
      } else if (socialNetwork === 'ok') {
        sendCommonEvents('oauth_start', 'ok');
        handleOKLogin(
          socialConfig as OKAuthConfig,
          (data) => {
            if (data.success) {
              pushAuthSuccessful(AuthMethodsForAnalytics.OK);
              sendCommonEvents('oauth', 'ok');
              handleOAuthSuccess(socialNetwork, {
                access_token: data.access_token,
                session_secret_key: data.session_secret_key,
                expires_in: data.expires_in,
              });
            }
          },
          () => {
            setRequestStatus(RequestStatus.Failed);
          },
        );
      } else if (socialNetwork === 'github') {
        sendCommonEvents('oauth_start', 'github');
        handleGithubLogin(
          socialConfig as GithubAuthConfig,
          (data) => {
            if (data.success && data.payload.code) {
              pushAuthSuccessful(AuthMethodsForAnalytics.Github);
              sendCommonEvents('oauth', 'github');
              handleOAuthSuccess(socialNetwork, {
                code: data.payload.code,
              });
            }
          },
          () => {
            setRequestStatus(RequestStatus.Failed);
          },
        );
      } else if (socialNetwork === 'tbank') {
        sendCommonEvents('oauth_start', 'tbank');
        handleTBankLogin(
          socialConfig as TBankAuthConfig,
          (data) => {
            const { code, state, session_state, redirect_url } = data.payload;
            if (
              data.success &&
              code &&
              state &&
              session_state &&
              redirect_url
            ) {
              pushAuthSuccessful(AuthMethodsForAnalytics.TBank);
              sendCommonEvents('oauth', 'tbank');
              handleOAuthSuccess(socialNetwork, {
                state,
                code,
                session_state,
                redirect_url,
              });
            }
          },
          () => {
            setRequestStatus(RequestStatus.Failed);
          },
        );
      } else if (socialNetwork === 'alfabank') {
        sendCommonEvents('oauth_start', 'alfabank');
        handleAlfaLogin(
          socialConfig as AlfaAuthConfig,
          (data) => {
            if (data.success && data.payload.code) {
              pushAuthSuccessful(AuthMethodsForAnalytics.Alfa);
              sendCommonEvents('oauth', 'alfabank');
              handleOAuthSuccess(socialNetwork, {
                state: data.payload.state,
                code: data.payload.code,
                redirect_url: data.payload.redirect_url,
              });
            }
          },
          () => {
            setRequestStatus(RequestStatus.Failed);
          },
        );
      } else if (socialNetwork === 'apple') {
        sendCommonEvents('oauth_start', 'apple');
        handleAppleLogin(
          socialConfig as AppleAuthConfig,
          (data) => {
            if (data.success) {
              pushAuthSuccessful(AuthMethodsForAnalytics.Apple);
              sendCommonEvents('oauth', 'apple');
              handleOAuthSuccess(
                socialNetwork,
                data.payload as AppleAuthResponse,
              );
            }
          },
          () => {
            setRequestStatus(RequestStatus.Failed);
          },
        );
      }
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error(error);
      setRequestStatus(RequestStatus.Failed);
    }
  };

  const handleOAuthSuccess = async (
    socialNetwork: SocialNetworkNames,
    response:
      | VKAuthResponse
      | YandexAuthResponse
      | OKAuthResponse
      | GithubAuthResponse
      | TBankAuthResponse
      | AppleAuthResponse,
  ): Promise<void> => {
    try {
      if (socialNetwork === 'vk') {
        await getOAuthVKLogin(response as VKAuthResponse);
      }

      if (socialNetwork === 'yandex') {
        await getOAuthYandexLogin(response as YandexAuthResponse);
      }

      if (socialNetwork === 'ok') {
        await getOAuthOKLogin(response as OKAuthResponse);
      }

      if (socialNetwork === 'github') {
        await getOAuthGithubLogin(response as GithubAuthResponse);
      }

      if (socialNetwork === 'tbank') {
        await getOAuthTBankLogin(response as TBankAuthResponse);
      }

      if (socialNetwork === 'alfabank') {
        await getOAuthAlfaLogin(response as AlfaAuthResponse);
      }

      if (socialNetwork === 'apple') {
        await getOAuthAppleLogin(response as AppleAuthResponse);
      }
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error('Не удалось войти через соцсеть');
      setRequestStatus(RequestStatus.Failed);
    }
  };

  const getOAuthVKLogin = async (token: VKAuthResponse): Promise<void> => {
    try {
      await flowAsync(
        authWithVKProvider,
        cond<PostClientAuthResponse, Promise<undefined> | undefined>([
          ifAuthNotSuccess(),
          ifHasRegistrationProcessedTrue(handleGetNewCode)(setSharedState)(
            setFormType,
          )(onSuccess),
          ifHasRegistrationProcessedFalse(
            setFormType,
            AuthProvider.VK,
          )(setSharedState),
        ]),
      )(token);
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error(e);
      setRequestStatus(RequestStatus.Failed);
    }
  };

  const getOAuthGithubLogin = async (
    token: GithubAuthResponse,
  ): Promise<void> => {
    try {
      await flowAsync(
        authWithGithubProvider,
        cond<PostClientAuthResponse, Promise<undefined> | undefined>([
          ifAuthNotSuccess(),
          ifHasRegistrationProcessedTrue(handleGetNewCode)(setSharedState)(
            setFormType,
          )(onSuccess),
          ifHasRegistrationProcessedFalse(
            setFormType,
            AuthProvider.Github,
          )(setSharedState),
        ]),
      )(token);
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error(e);
      setRequestStatus(RequestStatus.Failed);
    }
  };

  const getOAuthTBankLogin = async (
    token: TBankAuthResponse,
  ): Promise<void> => {
    try {
      await flowAsync(
        authWithTBankProvider,
        cond<PostClientAuthResponse, Promise<undefined> | undefined>([
          ifAuthNotSuccess(),
          ifHasRegistrationProcessedTrue(handleGetNewCode)(setSharedState)(
            setFormType,
          )(onSuccess),
          ifHasRegistrationProcessedFalse(
            setFormType,
            AuthProvider.TBank,
          )(setSharedState),
        ]),
      )(token);
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error(e);
      setRequestStatus(RequestStatus.Failed);
    }
  };

  const getOAuthAlfaLogin = async (token: AlfaAuthResponse): Promise<void> => {
    try {
      const authResponse = await authWithAlfaProvider(token);

      if (!authResponse.success) {
        return;
      }

      // Если клиент зарегистрирован
      if (authResponse.client.has_registration_processed) {
        if (isPhoneNotConfirm(authResponse)) {
          setSharedState(
            mergeFullClientDataWithSharedState(authResponse.client),
          );
          onSuccess(authResponse.client);
          await handleGetNewCode({
            phone: resetPhoneFormat(
              authResponse.client.unconfirmed_phone || '',
            ),
            event: 'sign_up',
            client_id: authResponse.client.id,
          });
          openConfirmPhoneWithSMSCode(setFormType)();
        } else if (isPhoneConfirm(authResponse)) {
          onSuccess(authResponse.client);
        }
      }
      // Если клиент новый и еще не проходил регистрацию
      else {
        setFormType(AuthFormType.Registration);
        setSharedState({
          password: '',
          email: authResponse.client.email || '',
          phone: authResponse.client.phone || '',
          surname: authResponse.client.surname || '',
          name: authResponse.client.name || '',
          id: authResponse.client.id,
          signUpType: AuthProvider.Alfa,
        });
      }
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error(e);
      setRequestStatus(RequestStatus.Failed);
    }
  };

  const getOAuthAppleLogin = async (
    token: AppleAuthResponse,
  ): Promise<void> => {
    try {
      await flowAsync(
        authWithAppleProvider,
        cond<PostClientAuthResponse, Promise<undefined> | undefined>([
          ifAuthNotSuccess(),
          ifHasRegistrationProcessedTrue(handleGetNewCode)(setSharedState)(
            setFormType,
          )(onSuccess),
          ifHasRegistrationProcessedFalse(
            setFormType,
            AuthProvider.Apple,
          )(setSharedState),
        ]),
      )(token);
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error(e);
      setRequestStatus(RequestStatus.Failed);
    }
  };

  const getOAuthYandexLogin = async (
    token: YandexAuthResponse,
  ): Promise<void> => {
    try {
      await flowAsync(
        authWithYandexProvider,
        cond<PostClientAuthResponse, Promise<undefined> | undefined>([
          ifAuthNotSuccess(),
          ifHasRegistrationProcessedTrue(handleGetNewCode)(setSharedState)(
            setFormType,
          )(onSuccess),
          ifHasRegistrationProcessedFalse(
            setFormType,
            AuthProvider.Yandex,
          )(setSharedState),
        ]),
      )(token);
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error(e);
      setRequestStatus(RequestStatus.Failed);
    }
  };

  const getOAuthOKLogin = async (token: OKAuthResponse): Promise<void> => {
    try {
      await flowAsync(
        authWithOKProvider,
        cond<PostClientAuthResponse, Promise<undefined> | undefined>([
          ifAuthNotSuccess(),
          ifHasRegistrationProcessedTrue(handleGetNewCode)(setSharedState)(
            setFormType,
          )(onSuccess),
          ifHasRegistrationProcessedFalse(
            setFormType,
            AuthProvider.OK,
          )(setSharedState),
        ]),
      )(token);
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error(e);
      setRequestStatus(RequestStatus.Failed);
    }
  };

  return {
    loginSocialNetwork,
  };
};
